import { comparisonTable } from '@/mocks/seoContent';

export default function WhatIsSection() {
  return (
    <section id="what-is" className="w-full py-16 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-xs md:text-sm font-medium text-primary-500 uppercase tracking-widest mb-3">Understanding the System</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
            What Is <em className="font-heading italic text-primary-500">ItaobuySpreadsheet</em>?
          </h2>
          <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-3xl mx-auto leading-relaxed">
            The <strong>itaobuyspreadsheet system</strong> is a modern procurement method that allows international shoppers to organize and purchase products from Chinese marketplaces like Taobao, 1688, and Weidian using a structured, spreadsheet-based ordering workflow. Instead of relying on traditional agents with opaque pricing, users maintain full control over every order.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-14">
          <div className="rounded-2xl overflow-hidden border border-background-200/60 bg-background-100">
            <div className="h-48 md:h-52">
              <img
                src="https://readdy.ai/api/search-image?query=A%20person%20browsing%20a%20laptop%20with%20a%20colorful%20online%20marketplace%20product%20grid%20visible%20on%20the%20screen%20coffee%20cup%20nearby%20on%20a%20warm%20wooden%20desk%20soft%20natural%20light%20from%20a%20nearby%20window%20cozy%20modern%20apartment%20vibe%20editorial%20lifestyle&width=600&height=420&seq=step-1-image-v2&orientation=landscape"
                alt="Browse and find products on Chinese marketplaces"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <span className="text-xl font-bold text-primary-600 font-heading">1</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Find Products</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Browse Taobao, 1688, or Weidian and locate the items you want — copy the product links just like you normally would when shopping on any marketplace.</p>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-background-200/60 bg-background-100">
            <div className="h-48 md:h-52">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20hands%20actively%20typing%20on%20a%20silver%20laptop%20with%20a%20spreadsheet%20visible%20on%20screen%20next%20to%20a%20notebook%20with%20product%20URLs%20scribbled%20on%20paper%20on%20a%20light%20oak%20desk%20bright%20natural%20daylight%20clean%20workspace&width=600&height=420&seq=step-2-image-v2&orientation=landscape"
                alt="Add product details into the spreadsheet"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                <span className="text-xl font-bold text-accent-600 font-heading">2</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Add to Spreadsheet</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Paste product links, specify variants, and enter quantities into your <strong>itaobuyspreadsheet</strong>. The structured format ensures every detail is captured correctly.</p>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-background-200/60 bg-background-100">
            <div className="h-48 md:h-52">
              <img
                src="https://readdy.ai/api/search-image?query=A%20clean%20desk%20with%20a%20laptop%20displaying%20a%20delivery%20tracking%20dashboard%20with%20progress%20bars%20and%20status%20icons%20next%20to%20a%20small%20shipping%20box%20and%20a%20phone%20showing%20a%20notification%20soft%20evening%20light%20warm%20satisfying%20vibe&width=600&height=420&seq=step-3-image-v2&orientation=landscape"
                alt="Submit and track your order in real time"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                <span className="text-xl font-bold text-secondary-600 font-heading">3</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Submit &amp; Track</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Submit your completed spreadsheet and receive real-time tracking updates. Every line item is verified, prices are locked, and nothing is hidden.</p>
            </div>
          </div>
        </div>

        <div id="comparison" className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/60">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
            <em className="italic text-primary-500">ItaobuySpreadsheet</em> vs Traditional Methods
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-background-300/60">
                  <th className="py-3 pr-4 text-sm font-semibold text-foreground-700 whitespace-nowrap">Feature</th>
                  <th className="py-3 px-4 text-sm font-semibold text-primary-600 whitespace-nowrap">ItaobuySpreadsheet</th>
                  <th className="py-3 px-4 text-sm font-semibold text-foreground-500 whitespace-nowrap">Traditional Agents</th>
                  <th className="py-3 pl-4 text-sm font-semibold text-foreground-500 whitespace-nowrap">Direct Taobao</th>
                </tr>
              </thead>
              <tbody>
                {comparisonTable.map((row) => (
                  <tr key={row.feature} className="border-b border-background-200/40 last:border-0">
                    <td className="py-3.5 pr-4 text-sm text-foreground-800 whitespace-nowrap">{row.feature}</td>
                    <td className="py-3.5 px-4 text-sm font-semibold text-primary-600 whitespace-nowrap">{row.itaobuy}</td>
                    <td className="py-3.5 px-4 text-sm text-foreground-500 whitespace-nowrap">{row.agents}</td>
                    <td className="py-3.5 pl-4 text-sm text-foreground-500 whitespace-nowrap">{row.direct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}